import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { toast } from "@/components/ui/use-toast"
import { useAsyncEffect } from "@/utils"
import { useState } from "preact/hooks"
import { api_fetch } from "../api/client.tsx"
import { Loader2 } from "lucide-react"

interface AccountingSkillsFileResponse {
  accounting_skills_file: string | null
}

export function AccountingSkillsFileEditor() {
  const [content, setContent] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isDirty, setIsDirty] = useState(false)

  useAsyncEffect(async () => {
    setIsLoading(true)
    const res = await api_fetch<AccountingSkillsFileResponse>("/organization/accounting-skills")
    if (res.ok) {
      setContent(res.value.data.accounting_skills_file ?? "")
    } else {
      toast({ title: "Error", description: "Failed to load CodingWiz knowledge base", variant: "destructive" })
    }
    setIsLoading(false)
  }, [])

  async function handleSave() {
    setIsSaving(true)
    const res = await api_fetch<AccountingSkillsFileResponse>("/organization/accounting-skills", {
      method: "PUT",
      body: { accounting_skills_file: content },
    })
    if (res.ok) {
      setIsDirty(false)
      toast({ title: "Saved", description: "CodingWiz knowledge base updated successfully" })
    } else {
      toast({ title: "Error", description: "Failed to save CodingWiz knowledge base", variant: "destructive" })
    }
    setIsSaving(false)
  }

  if (isLoading) {
    return (
      <div class="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div class="space-y-3">
      <p class="text-sm text-gray-500">
        Notes used by <strong>CodingWiz</strong> for single ledger coding and multi-line splits—how your brand
        classifies charges, preferred split grouping, GL conventions, and exceptions. This is separate from the
        general AI knowledge base on the Organization page.
      </p>
      <Textarea
        value={content}
        onChange={(e: any) => {
          setContent(e.target.value)
          setIsDirty(true)
        }}
        onKeyDown={async (e: any) => {
          if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
            e.preventDefault()
            if (isDirty && !isSaving) {
              await handleSave()
            }
          }
        }}
        // @ts-ignore
        rows={12}
        class="font-mono text-sm"
        placeholder={
          "Example:\n- Map all MCB / scan deductions to the Scans GL bucket unless the backup says display.\n- Slotting is always 45xx Trade Spend : Slotting."
        }
      />
      <div class="flex justify-end">
        <Button
          // @ts-ignore
          size="sm"
          onClick={handleSave}
          disabled={isSaving || !isDirty}
        >
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
          Save
        </Button>
      </div>
    </div>
  )
}
